import React, { useMemo } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { IconChevronDown } from '@tabler/icons-react';
import type { PropertyDetailsFragment } from '__generated__/athena/graphql';
import LongTextNew from 'components/properties/shared/LongTextNew';
import { Divider } from 'components/shared/Divider';
import { formatCurrency } from 'helpers/formatters';

import { convertDwellingType } from './helpers';

interface HomeDetailsProps {
  property: PropertyDetailsFragment;
}

const convertKitchenCountertop = (kitchenCountertop: string) => {
  switch (kitchenCountertop) {
    case 'GRANITE_SLAB':
      return 'Granite slab';
    case 'GRANITE_TILE':
      return 'Granite tile';
    case 'QUARTZ':
      return 'Quartz';
    case 'CORIAN':
      return 'Corian';
    case 'FORMICA':
      return 'Formica';
    default:
      return 'Other';
  }
};

const convertMasterBedroomLocation = (
  masterBedroomLocation?: string | null,
  exteriorStories?: number | null,
) => {
  switch (masterBedroomLocation) {
    case 'FIRST_FLOOR':
      if (exteriorStories && exteriorStories > 1) {
        return 'Downstairs bedroom';
      }
      return 'Main bedroom';
    case 'SECOND_FLOOR':
    case 'THIRD_FLOOR':
      return 'Upstairs bedroom';
    default:
      return null;
  }
};

export const HomeDetails: React.FC<HomeDetailsProps> = ({ property }: HomeDetailsProps) => {
  const masterBedroomLocation = convertMasterBedroomLocation(
    property.masterBedroomLocation,
    property.exteriorStories,
  );

  const rows: Array<{ title: string; value: string | number | null }> = [
    {
      title: 'Home type',
      value: property?.dwellingType ? convertDwellingType(property.dwellingType) : null,
    },
    {
      title: 'Lot size',
      value: property?.sqFtLot ? `${property.sqFtLot.toLocaleString()} sqft lot` : null,
    },
    {
      title: 'Price per sqft',
      value: property?.pricePerSqFt
        ? `${formatCurrency(property.pricePerSqFt, { inDollars: true })} / sqft`
        : null,
    },
    {
      title: 'Built in',
      value: property?.yearBuilt ?? null,
    },
    {
      title: 'Kitchen',
      value: property?.kitchenCountertop
        ? convertKitchenCountertop(property.kitchenCountertop)
        : null,
    },
    {
      title: 'Garage',
      value: property?.garageSpaces ? `${property.garageSpaces} car garage` : null,
    },
    {
      title: 'Master bedroom',
      value: masterBedroomLocation,
    },
  ];

  const description = useMemo(() => {
    const filteredRows = rows.filter((row) => row.value !== null);
    const showPropertyDescription = property.listingState === 'ON_THE_MARKET';
    return (
      <Col className="w-full" id="homeDetails">
        {showPropertyDescription && property?.description && (
          <Col className="pb-6 max-md:pb-11">
            <LongTextNew content={property?.description} limit={212} />
          </Col>
        )}
        {filteredRows.map((row, index) =>
          row.value ? (
            <Col key={`homeDetail_${index}`}>
              <Row className="w-full justify-between">
                <Text className="text-body-sm text-od-content-primary">{row.title}</Text>
                <Text className="text-label-sm text-od-content-primary">{row.value}</Text>
              </Row>
              {index !== filteredRows.length - 1 ? <Divider className="py-4" /> : null}
            </Col>
          ) : null,
        )}
        {property?.sellerDisclosuresDownloadUrl && (
          <Col className="py-6">
            <Button
              analyticsName="seller_disclosure_link"
              href={property.sellerDisclosuresDownloadUrl}
              external
              size="lg"
              variant="secondary"
              aria-label="Download seller’s disclosure"
              className="w-full"
            >
              Download seller&rsquo;s disclosure
            </Button>
          </Col>
        )}
      </Col>
    );
  }, [rows]);

  return (
    <Collapsible.Root defaultOpen className="w-full">
      <Collapsible.Trigger className="group flex w-full items-center justify-between gap-2 py-6">
        <Text
          as="span"
          className="text-left text-label-xl text-od-content-primary lg:text-subheader-xs"
        >
          Home details
        </Text>
        <Box className="shrink-0 text-od-content-primary transition-transform duration-200 group-data-[panel-open]:rotate-180">
          <IconChevronDown size={20} />
        </Box>
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-4">{description}</Collapsible.Panel>
    </Collapsible.Root>
  );
};
